import { useQueryClient } from "@tanstack/react-query";

import { DraftListSortOption } from "components/drafts/UserDraftSortSelect";
import { DraftEventListSortOption } from "components/events/UserDraftEventSortSelect";
import {
  getGetSettingQueryKey,
  useGetSetting,
  useUpdateSetting,
} from "generated/api/user-settings/user-settings";
import { UpdateSettingBody } from "generated/model";
import { VivaEngageHomePostFeedTabs } from "components/integrations/vivaEngage/VivaEngageHomePostFeed";

// Note: Currently, this interface only exists here on the front-end to have autocompletion on keys
// and values and to avoid mistakes, but on the backend and the generated API it's free JSON data.
// Should be updated with any new setting.
interface BaseUserSettings {
  introVideoHidden?: boolean;
  autoDismissSnackbars?: boolean;
  networkSettings?: Record<string, NetworkSettings | undefined>;
  approvedEventDisclosureAgreements?: string[];
  draftListSort?: {
    sortBy: DraftListSortOption;
    direction?: "asc" | "desc";
  };
  hidePublishedDrafts?: boolean;
  eventDraftListSort?: {
    sortBy: DraftEventListSortOption;
    direction?: "asc" | "desc";
  };
  showAdminSideBar?: boolean;
  hidePublishedEventDrafts?: boolean;
  distributionListSort?: {
    sortBy:
      | "customEmailSubject"
      | "distributionDate"
      | "triggeredByPerson"
      | "totalRecipients";
    direction?: "asc" | "desc";
  };
  postsOnClick?: boolean;
  globalCalendarFilters?: CalendarFilters;
  language?: string;
  closedMSTeamsAttendanceBanner?: boolean;
  networkListSelectedTab?: "ALL_NETWORKS" | "MY_NETWORKS";
  postFeedVivaEngageTab?: VivaEngageHomePostFeedTabs;
  pushNotificationId?: string;
  memberInsightsSeenNetworkIds?: string[];
}

interface CalendarFilters {
  isListView?: boolean;
  eventTypes?: "ALL_EVENTS" | "NETWORK_EVENTS" | "REGISTERED_EVENTS";
  dateFilter?: string;
  dateRangeType?:
    "PAST_EVENTS" | "UPCOMING_EVENTS" | "BEFORE_DATE" | "AFTER_DATE";
  hiddenCalendars?: number[];
}

interface NetworkSettings {
  introVideoHidden?: boolean;
}

export function useUserSetting<
  K extends keyof BaseUserSettings,
  T extends BaseUserSettings[K] = BaseUserSettings[K],
>(
  key: K,
  options?: Parameters<typeof useGetSetting<T>>[1],
): [T | undefined, (value: T | undefined) => void, boolean] {
  const { data, isLoading } = useGetSetting<T>(key, {
    query: { gcTime: Infinity, staleTime: Infinity },
    ...options,
  });
  const queryClient = useQueryClient();
  const queryKey = getGetSettingQueryKey(key);

  /*
   * Optimistically update a specific user setting, and if it fails (after all attempts) rollback to
   * the previous value and show error message.
   *
   * <p> For more about the pattern used here see
   * {@link https://tanstack.com/query/v4/docs/react/guides/optimistic-updates|Optimistic Updates}
   *
   * @param key the key of the setting to update.
   */
  const { mutate } = useUpdateSetting({
    mutation: {
      onMutate: async (newValue) => {
        await queryClient.cancelQueries({ queryKey });
        const previousValue = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(queryKey, newValue.data);
        return { previousValue };
      },
      onError: (_err, _newValue, context) => {
        queryClient.setQueryData(queryKey, context?.previousValue);
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey: queryKey });
      },
    },
  });

  return [
    data,
    (value: T | undefined) =>
      mutate({ key, data: value as unknown as UpdateSettingBody }),
    isLoading,
  ];
}

export function useNetworkUserSetting<
  K extends keyof NetworkSettings,
  T extends NetworkSettings[K] = NetworkSettings[K],
>(
  networkId: string | undefined,
  key: K,
  options?: Parameters<
    typeof useGetSetting<BaseUserSettings["networkSettings"]>
  >[1],
): [T | undefined, (value: T | undefined) => void, boolean] {
  const [networkSettings, setNetworkSettings, isLoading] = useUserSetting(
    "networkSettings",
    { query: { enabled: networkId !== undefined }, ...options },
  );

  if (networkId === undefined) {
    return [
      undefined,
      () => {
        /* No Op */
      },
      false,
    ];
  }

  const settingValue = networkSettings?.[networkId]?.[key];

  return [
    settingValue as T,
    (value: NetworkSettings[K] | undefined) => {
      setNetworkSettings({
        ...networkSettings,
        [networkId]: { ...networkSettings?.[networkId], [key]: value },
      });
    },
    isLoading,
  ];
}
